import React, { ReactNode } from "react";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import { addSchemeToUrlIfEmpty } from "@mapmycustomers/shared";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { stopEventPropagation as stopEvents } from "@mapmycustomers/shared/util/browser";
import {
  TEAM_MENTION_PREFIX,
  USER_MENTION_PREFIX,
} from "@mapmycustomers/shared/util/consts/mentions";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import styles from "@app/component/Lexer/Lexer.module.scss";
import { tokenize } from "@app/component/Lexer/tokenizer";

export interface LexerOptions {
  isMentionDisabled?: (user: User) => boolean;
  mentionClassName?: string;
  stopEventPropagation?: boolean;
  teams?: Team[];
  users?: User[];
}

export const lexizeText = (
  text: string,
  options?: LexerOptions,
  keyPrefix = "" // scopes keys when a caller merges tokens into a shared sibling list
): ReactNode[] => {
  const tokens = tokenize(text);
  return tokens.map((token, index) => {
    switch (token.type) {
      case "url":
        return (
          <a
            href={addSchemeToUrlIfEmpty(token.url)}
            key={`${keyPrefix}token-url__${index}`}
            onClick={options?.stopEventPropagation ? stopEvents : undefined}
            rel="noopener noreferrer"
            target="_blank"
          >
            {token.url}
          </a>
        );
      case "email":
        return (
          <a
            href={`mailto:${token.email}`}
            key={`${keyPrefix}token-email__${index}`}
            onClick={options?.stopEventPropagation ? stopEvents : undefined}
          >
            {token.email}
          </a>
        );
      case "phone":
        return (
          <a
            href={`tel:${token.phone.startsWith("+") ? token.phone : `+${token.phone}`}`}
            key={`${keyPrefix}token-phone__${index}`}
            onClick={options?.stopEventPropagation ? stopEvents : undefined}
          >
            {token.phone}
          </a>
        );
      case "user": {
        const user = options?.users?.find(({ username }) => username === token.username);
        if (user) {
          return (
            <Tooltip
              className={cn(styles.mention, options?.mentionClassName, {
                [styles.mentionDisabled]:
                  user && options?.isMentionDisabled ? options.isMentionDisabled(user) : false,
              })}
              key={`${keyPrefix}token-mention__${index}`}
              overlayClassName={styles.mentionOverlay}
              title={token.username}
            >
              {userDisplayName(user)}
            </Tooltip>
          );
        } else {
          // just return as a string
          return `${USER_MENTION_PREFIX}${token.username}`;
        }
      }
      case "team": {
        const team = options?.teams?.find(({ id }) => id === token.teamId);
        if (team) {
          return (
            <Tooltip
              className={cn(styles.mention, options?.mentionClassName, {
                [styles.mentionDisabled]: !!team.deletedAt,
              })}
              key={`${keyPrefix}token-mention__${index}`}
              overlayClassName={styles.mentionOverlay}
              title={team.name}
            >
              {team.name}
            </Tooltip>
          );
        } else {
          // just return as a string
          return `${TEAM_MENTION_PREFIX}${token.teamId}`;
        }
      }
      case "text": {
        return token.value;
      }
    }
  });
};
